<template>
  <div>
    <v-radio-group v-model="mode" row dense hide-details class="mt-0 mb-2">
      <v-radio
        :label="$t('dangerousGoods.dangerousGoodsList.form.operatingInstructionLink')"
        value="existing"
      ></v-radio>
      <v-radio
        :label="$t('dangerousGoods.dangerousGoodsList.form.operatingInstructionUpload')"
        value="upload"
      ></v-radio>
      <v-radio
        :label="$t('dangerousGoods.dangerousGoodsList.form.operatingInstructionNone')"
        value="none"
      ></v-radio>
    </v-radio-group>

    <v-autocomplete
      v-if="mode === 'existing'"
      v-model="selected"
      :items="documentItems"
      :filter="filterDocuments"
      item-text="name"
      item-value="key"
      :placeholder="$t('dangerousGoods.dangerousGoodsList.form.operatingInstructionSelect')"
      :loading="loadingDocuments"
      :no-data-text="$t('general.noData')"
      data-cy="operatingInstruction"
      clearable
      dense
    >
      <template v-slot:item="{ item }">
        <div class="d-flex flex-column py-1">
          <div>{{ item.name }}</div>
          <div class="text--secondary text-caption">{{ item.subtitle }}</div>
        </div>
      </template>
    </v-autocomplete>

    <v-row v-else-if="mode === 'upload'" dense align="center">
      <v-col cols="12" md="6">
        <v-file-input
          v-model="file"
          accept="application/pdf"
          prepend-icon=""
          prepend-inner-icon="mdi-paperclip"
          :label="$t('operatingInstructions.addFile')"
          dense
          hide-details
          @change="handleFile"
        ></v-file-input>
      </v-col>
      <v-col cols="12" md="6">
        <v-combobox
          v-model="categoryName"
          :items="categoryNames"
          :label="$t('dangerousGoods.dangerousGoodsList.form.instructionCategory')"
          :loading="loadingCategories"
          dense
          hide-details
          @change="syncPendingUpload"
        ></v-combobox>
      </v-col>
    </v-row>
  </div>
</template>
<script>
export default {
  props: ["value"],
  data: () => ({
    mode: "existing",
    uploads: [],
    betriebsanweisungen: [],
    categories: [],
    loadingDocuments: false,
    loadingCategories: false,
    file: null,
    fileContent: null,
    categoryName: null,
  }),
  computed: {
    categoryNameById() {
      const map = {};
      for (const category of this.categories) {
        map[category._id || category.id] = category.name;
      }
      return map;
    },
    categoryNames() {
      return this.categories.map((category) => category.name);
    },
    documentItems() {
      //uploaded files and generated documents are one and the same to the user
      const uploads = this.uploads.map((item) => ({
        key: `oi:${item._id || item.id}`,
        name: item.name,
        category: this.categoryNameById[this.idOf(item.instructionCategory)] || "",
      }));
      const generated = this.betriebsanweisungen.map((item) => ({
        key: `ba:${item._id || item.id}`,
        name: item.title,
        category: this.categoryNameById[this.idOf(item.instructionCategory)] || "",
      }));

      return [...uploads, ...generated]
        .map((item) => ({ ...item, subtitle: item.category }))
        .sort(
          (a, b) =>
            String(a.category).localeCompare(String(b.category), undefined, {
              sensitivity: "base",
            }) ||
            String(a.name ?? "").localeCompare(String(b.name ?? ""), undefined, {
              sensitivity: "base",
              numeric: true,
            }),
        );
    },
    //the two references are mutually exclusive, the select works on one key
    selected: {
      get() {
        if (this.value?.operatingInstruction) {
          return `oi:${this.idOf(this.value.operatingInstruction)}`;
        }
        if (this.value?.betriebsanweisung) {
          return `ba:${this.idOf(this.value.betriebsanweisung)}`;
        }
        return null;
      },
      set(key) {
        if (!key) {
          this.value.operatingInstruction = null;
          this.value.betriebsanweisung = null;
          return;
        }
        const [type, id] = key.split(":");
        this.value.operatingInstruction = type === "oi" ? id : null;
        this.value.betriebsanweisung = type === "ba" ? id : null;
      },
    },
  },
  watch: {
    mode(value) {
      //only one of the two ways can be in effect at a time
      if (value !== "upload") {
        this.file = null;
        this.fileContent = null;
        this.value.pendingOperatingInstruction = null;
      }
      if (value !== "existing") {
        this.selected = null;
      }
    },
  },
  created() {
    this.loadDocuments();
    this.loadCategories();
  },
  methods: {
    idOf(reference) {
      //the field may arrive populated or as a plain id
      return typeof reference === "object" && reference !== null
        ? reference._id || reference.id
        : reference;
    },
    unwrap(response) {
      return response?.data || response || [];
    },
    //searches name, category and type, so a document can be found by its folder
    filterDocuments(item, queryText) {
      const haystack = `${item.name} ${item.subtitle}`.toLowerCase();
      return queryText
        .toLowerCase()
        .split(" ")
        .filter(Boolean)
        .every((term) => haystack.includes(term));
    },
    async loadDocuments() {
      this.loadingDocuments = true;
      try {
        const [uploads, generated] = await Promise.all([
          this.$store.dispatch("getOperatingInstructions", {}),
          this.$store.dispatch("getBetriebsanweisungen", {}),
        ]);
        this.uploads = this.unwrap(uploads);
        this.betriebsanweisungen = this.unwrap(generated);
      } finally {
        this.loadingDocuments = false;
      }
    },
    async loadCategories() {
      this.loadingCategories = true;
      try {
        this.categories = this.unwrap(
          await this.$store.dispatch("getAllInstructionCategories"),
        );
      } finally {
        this.loadingCategories = false;
      }
    },
    async handleFile(fileObject) {
      if (!fileObject) {
        this.fileContent = null;
        this.syncPendingUpload();
        return;
      }
      this.fileContent = await this.readFile(fileObject);
      this.syncPendingUpload();
    },
    readFile(fileObject) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(fileObject);
      });
    },
    //handed to the form, which creates the operating instruction on save
    syncPendingUpload() {
      if (!this.file || !this.fileContent) {
        this.value.pendingOperatingInstruction = null;
        return;
      }
      this.value.pendingOperatingInstruction = {
        name: this.file.name,
        filetype: this.file.name.match(/\.[0-9a-z]+$/i)[0].split(".")[1],
        content: this.fileContent,
        categoryName: (this.categoryName || "").trim(),
      };
    },
  },
};
</script>
